import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { distinctUntilObjectChanged, ignoreNullish } from '@aiwyn/shared';
import { skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { StripePlatformService } from '../../services';
import { PaymentMethodMode } from '../../../models';
import { StripePaymentElementComponent } from '../stripe-payment-element/stripe-payment-element.component';
import { StripeAddressElementComponent } from '../stripe-address-element/stripe-address-element.component';

@Component({
  selector: 'app-stripe-select-new-payment-method',
  templateUrl: './stripe-select-new-payment-method.component.html',
  styleUrls: ['./stripe-select-new-payment-method.component.scss'],
  standalone: false,
})
export class StripeSelectNewPaymentMethodComponent implements OnDestroy {
  @Input() userProvidedEmail: string;
  @Output() dirtied = new EventEmitter<void>();

  private readonly _unsubscribe$ = new Subject<void>();
  PaymentMethodMode = PaymentMethodMode;

  @ViewChild(StripePaymentElementComponent) stripePaymentElement: StripePaymentElementComponent;
  @ViewChild(StripeAddressElementComponent) stripeAddressElement: StripeAddressElementComponent;

  dirtiedMonitor$ = merge(
    this.stripePlatformService.paymentElement$.pipe(
      ignoreNullish(),
      // On element initialization, change will emit twice. We want to ignore both since they aren't caused by user interaction.
      switchMap(_ => this.stripePlatformService.onPaymentElementChange$.pipe(
        ignoreNullish(),
        skip(1), // Stripe emits an inaccurate change event on initialization (says empty = false)
        distinctUntilObjectChanged(),
        skip(1), // skip first pass from distinctUntilChanged (initial value not caused by user interaction)
        take(1),
      )),
    ),
    this.stripePlatformService.addressElement$.pipe(
      ignoreNullish(),
      switchMap(_ => this.stripePlatformService.onAddressElementChange$.pipe(
        ignoreNullish(),
        distinctUntilObjectChanged(),
        skip(1), // skip first pass from distinctUntilChanged (initial value not caused by user interaction)
        take(1),
      )),
    ),
  ).pipe(
    tap(_ => this.dirtied.emit()),
    takeUntil(this._unsubscribe$),
  );

  addMargin$ = new Subject<boolean>();

  constructor(
    private readonly stripePlatformService: StripePlatformService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.dirtiedMonitor$.subscribe();
    this.stripePlatformService.isAddPaymentMethodFlowActive$.next(true);
  }

  onAddressElementVisibleChange(visible: boolean): void {
    this.addMargin$.next(visible);
    this.changeDetectorRef.detectChanges();
  }

  onClose(): Observable<unknown> {
    return combineLatest([this.stripePaymentElement.onClose(), this.stripeAddressElement.onClose()]);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.stripePlatformService.isAddPaymentMethodFlowActive$.next(false);
  }
}
